import React, { useState } from 'react';
import ControlPanelSuccessIcon from 'components/atoms/icons/controlPanelSuccessIcon';
import { useTranslation } from 'react-i18next';
import { ModuleType } from 'infrastructure/enums';
import DischargePatientModal from 'components/organisms/controlPanel/dischargePatientModal';
import DischargePatientModalMobile from 'components/organisms/controlPanel/dischargePatientModalMobile';
import useUser from 'utils/useUser';
import forms from 'constants/forms';
import { useUpdatePatientDischargeStatus } from 'infrastructure/hooks/control-panel/use-update-discharge-status';
import { useUpdatePatientCcmStatus } from 'infrastructure/hooks/control-panel';
import PatientControlButton from 'components/atoms/patentControlButton';
import CrossControlIcon from 'components/atoms/icons/crossControlIcon';
import BaseGrid from 'components/atoms/base-grid';
import { useAccess } from 'infrastructure/hooks/access/useAccess';

interface IProps {
  module: ModuleType;
  patientGuid: string;
}

const UnenrollPatientControl: React.FC<IProps> = (props) => {
  const { module, patientGuid } = props;

  const { t } = useTranslation();

  const [dischargePopupOpen, setDischargePopupOpen] = useState(false);
  const [ccmDischargePopupOpen, setCcmDischargePopupOpen] = useState(false);

  const { isMobileDevice } = useUser();

  const {
    isCombinedModule,
    isRpmModule,
    isCcmModule,
    isBlockCcmModule,
    isReadOnlyRole,
    isCcmActionAllowed,
    isCcmActionDisabled,
    isActiveRpmStatus,
    isEnrolledCcmStatus,
  } = useAccess({
    module,
    patientGuid,
  });

  const { updateDischargeStatus, updateDischargeStatusLoading } =
    useUpdatePatientDischargeStatus({
      onSuccess() {
        setDischargePopupOpen(false);
      },
    });

  const { updateCcmStatus, ccmStatusLoading } = useUpdatePatientCcmStatus({
    onSuccess() {
      setCcmDischargePopupOpen(false);
    },
  });

  const rpmLabel = isActiveRpmStatus
    ? `${t('labels.requestDischarge')} ${t('labels.rpm')}`
    : `${t('labels.dischargeRequested')} ${t('labels.rpm')}`;

  const ccmLabel = isEnrolledCcmStatus
    ? `${t('labels.unenrollPatient')} ${t('labels.ccm')}`
    : `${t('labels.patientUnenrolled')} ${t('labels.ccm')}`;

  const rpmDisable = isReadOnlyRole || !isActiveRpmStatus;

  const ccmDisable =
    (isCombinedModule &&
      (isBlockCcmModule || isReadOnlyRole || !isCcmActionAllowed)) ||
    isCcmActionDisabled;

  const rpmRender = isRpmModule || isCombinedModule;

  const ccmRender = !isMobileDevice && (isCcmModule || isCombinedModule);

  const getIcon = (active: boolean) =>
    active ? ControlPanelSuccessIcon : CrossControlIcon;

  const columns = isMobileDevice || !isCombinedModule ? 1 : 2;

  return (
    <>
      <BaseGrid columns={columns}>
        {rpmRender && (
          <PatientControlButton
            label={rpmLabel}
            Icon={getIcon(isActiveRpmStatus)}
            loading={updateDischargeStatusLoading}
            disabled={rpmDisable}
            handleClick={() => setDischargePopupOpen(true)}
          />
        )}
        {ccmRender && (
          <PatientControlButton
            label={ccmLabel}
            Icon={getIcon(isEnrolledCcmStatus)}
            loading={ccmStatusLoading}
            disabled={ccmDisable}
            handleClick={() => setCcmDischargePopupOpen(true)}
          />
        )}
      </BaseGrid>

      {isMobileDevice && (
        <DischargePatientModalMobile
          isOpen={dischargePopupOpen}
          toggle={setDischargePopupOpen}
          patientGuid={patientGuid}
          options={forms.patientDischargeStatuses}
          loading={updateDischargeStatusLoading}
          submit={updateDischargeStatus}
          modalType={ModuleType.RPM}
        />
      )}
      {!isMobileDevice && (
        <DischargePatientModal
          isOpen={dischargePopupOpen}
          toggle={setDischargePopupOpen}
          patientGuid={patientGuid}
          options={forms.patientDischargeStatuses}
          loading={updateDischargeStatusLoading}
          submit={updateDischargeStatus}
          modalType={ModuleType.RPM}
        />
      )}

      {isMobileDevice && (
        <DischargePatientModalMobile
          isOpen={ccmDischargePopupOpen}
          toggle={setCcmDischargePopupOpen}
          patientGuid={patientGuid}
          options={forms.patientCcmDischargeStatuses}
          loading={ccmStatusLoading}
          submit={updateCcmStatus}
          modalType={ModuleType.CCM}
        />
      )}
      {!isMobileDevice && (
        <DischargePatientModal
          isOpen={ccmDischargePopupOpen}
          toggle={setCcmDischargePopupOpen}
          patientGuid={patientGuid}
          options={forms.patientCcmDischargeStatuses}
          loading={ccmStatusLoading}
          submit={updateCcmStatus}
          modalType={ModuleType.CCM}
        />
      )}
    </>
  );
};

export default UnenrollPatientControl;
